import { useContext, useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ServiceContext } from "../../../contexts/ServiceContext";
import { ReactComponent as DescriptionIcon } from "../../../icons/description-settings.svg";
import { AppAlert } from "../../../utils/AppAlert";
import { ApiError } from "../../../services/ApiError";
import Select from "react-select";
import config from "../../../config";
import "./device.css";

function Device() {
  const { infrastructureService } = useContext(ServiceContext);
  const [loading, setLoading] = useState(true);
  const [nextGeneration, setNextGeneration] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [name, setName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [description, setDescription] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [channelName1, setChannelName1] = useState("");
  const [channelName2, setChannelName2] = useState("");
  const [channelName3, setChannelName3] = useState("");
  const [channelName4, setChannelName4] = useState("");
  const [companyId, setCompanyId] = useState(0);
  const [companies, setCompanies] = useState([]);
  const userRole = localStorage.getItem("user_role");
  const Global_Admin_Role = "Global Admin";

  const { deviceId } = useParams();

  const navigate = useNavigate();
  
  const deviceTypeOptions = [
      {
          value: true,
          label: "Next-Generation",
      },
      {
          value: false,
          label: "Original",
      },
  ];

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#f5f5f5",
    borderColor: state.isFocused ? "#c7c4c4ff" : "#ccc",
    boxShadow: state.isFocused ? "0 0 0 2px #b1aeaeff" : "none",
    "&:hover": { borderColor: "#3f3f3fff" },
    borderRadius: "8px",
    padding: "0 4px",       
    minHeight: "45px",      
    width: "100%"
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "45px",         
    padding: "0 6px",
    alignItems: "center",  
    justifyContent: "center",  
    paddingBottom: "10px" 
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    height: "100%"
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#525050"
      : state.isFocused
      ? "#ebe8e8ff"
      : "white",
    color: state.isSelected ? "white" : "#525050",
    padding: "5px 10px",   
    cursor: "pointer",
    fontWeight: state.isSelected ? "bold" : "normal",
    transition: "background-color 0.1s ease",
    ":active": {
      backgroundColor: "#525050",
      color: "white"
    }
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#888",
    fontStyle: "italic",
    textAlign: "center",
    margin: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#222",
    margin: 0,
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? "#3f3f3fff" : "#999",
    "&:hover": { color: "#3f3f3fff" }
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "4px",
    boxShadow: "0 4px 12px #525050"
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 0
  })
};

function validateDevice({ name, companyId, serialNumber, alarmChannels }) {
  if (!name || name.trim() === "") {
    setErrorMessage("Name is required");
    return false;
  }
  if (deviceId === "new" && (!companyId || companyId === 0)) {
    setErrorMessage("Company is required");
    return false;
  }
  if (deviceId === "new" && (!serialNumber || serialNumber.trim() === "")) {
    setErrorMessage("Serial number is required");
    return false;
  }
  if (!alarmChannels || alarmChannels.some(channel => !channel.name || channel.name.trim() === "")) {
    setErrorMessage("All alarm channels must have a name");
    return false;
  }
  return true;
}
 
  
  async function handleSaveClick() {
    let alarmChannels = [
        {
            name: channelName1,
            alarmChannelId: 1
        },
        {
            name: channelName2,
            alarmChannelId: 2
        },
        {
            name: channelName3,
            alarmChannelId: 3
        },
        {
            name: channelName4,
            alarmChannelId: 4
        }
    ]

    if(deviceId === "new"){        
      let effectiveCompanyId = companyId;

      if(userRole !== Global_Admin_Role && companies?.length > 0) {
           effectiveCompanyId = companies[0].id;
           setCompanyId(effectiveCompanyId);   
      }
      
      let addDevice = {
          name,
          companyId: effectiveCompanyId,
          description,
          serialNumber,
          nextGeneration,
          alarmChannels,
      };
      
      try {
        setLoadingSave(true); 
        if (!validateDevice(addDevice)) return;
        await infrastructureService.put("Device", addDevice);
        navigate(-1);
        AppAlert.success("The device was added successfully");
      } catch (err) {
        if (err instanceof ApiError) {
            if (err.errors) {
              setErrorMessage(Object.values(err.errors).flat().join("\n"));
            } else {
              setErrorMessage(err.message);
            }
          } else {
            setErrorMessage(err.message || "Unexpected error");
          }
      }finally{
        setLoadingSave(false);
      }
    }else{
      let editDevice = {
        id: deviceId,
        name: name,
        description: description,
        alarmChannels: alarmChannels
      };
      try {
        setLoadingSave(true); 
        if (!validateDevice(editDevice)) return;
        await infrastructureService.patch("Device", editDevice);
        navigate(-1);
        AppAlert.success("Success! Changes have been applied.");
      } catch (err) {
        if (err instanceof ApiError) {
            if (err.errors) {
              setErrorMessage(Object.values(err.errors).flat().join("\n"));
            } else {
              setErrorMessage(err.message);
            }
          } else {
            setErrorMessage(err.message || "Unexpected error");
          }
      }finally{
        setLoadingSave(false);
      }
    }
  }
  
  function handleCancelClick() {
    navigate(-1);
  }

  const handleChange = (selectedOption) => {
    setCompanyId(selectedOption?.value || null);
  };

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      let getCompanies = {
        filter: "",
        showBlocked: false,
        fromIndex: 0,
        itemsCount: config.uiSettings.itemsPerPage
      };
      const data = await infrastructureService.get("Companies", getCompanies); 
      setCompanies(data.items);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  }, [infrastructureService]);
  
  useEffect(() => {
    if(deviceId !== "new"){
      const fetchDevice = async () => {
        let device = {
          deviceId: deviceId
        };
        try {
          const data = await infrastructureService.get("Device", device); 
          setName(data.name);
          setCompanyId(data.companyId);
          setDescription(data.description);
          setSerialNumber(data.serialNumber);
          setNextGeneration(data.nextGeneration);
          setChannelName1(data?.alarmChannels?.find(c => c.alarmChannelId === 1)?.name || null);
          setChannelName2(data?.alarmChannels?.find(c => c.alarmChannelId === 2)?.name || null);
          setChannelName3(data?.alarmChannels?.find(c => c.alarmChannelId === 3)?.name || null);
          setChannelName4(data?.alarmChannels?.find(c => c.alarmChannelId === 4)?.name || null);
        } catch (error) {
          console.error("Error fetching device:", error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchDevice();
    }else{
      setChannelName1("Channel 1");
      setChannelName2("Channel 2");
      setChannelName3("Channel 3");
      setChannelName4("Channel 4");
      setLoading(false);
    }
    loadCompanies();
  }, [infrastructureService, deviceId, loadCompanies]);
  
  if (loading) return <div>Loading...</div>;

  return (   
       <div className='device-settings'>
        <div className="container">
          <div className="card">
            <div className="text">Device Settings</div>
          </div>
          <div className="inputs">
            <div className="input">
              <DescriptionIcon/>
              <input placeholder="Name" onChange={(e) => setName(e.target.value)} value={name}/>
            </div>
            <div className="input">
              <DescriptionIcon/>
              <input placeholder="Description" onChange={(e) => setDescription(e.target.value)} value={description}/>
            </div>
            <div className="input">
              <DescriptionIcon/>
              <input placeholder="ESN (Ex: 0-1234567)" onChange={(e) => setSerialNumber(e.target.value)} value={serialNumber} disabled={deviceId !== "new"}/>
            </div>
            <div className="input settings-select">
              <div className="select-company-d">
                <Select
                  options={deviceTypeOptions}
                  styles={customStyles}
                  isSearchable={false}
                  value={deviceTypeOptions.find(
                    (option) => option.value === nextGeneration,
                  )}
                  onChange={(option) => setNextGeneration(option?.value ?? true)}
                  isDisabled={deviceId !== "new"}
                />
              </div>
            </div>
            {userRole !== undefined && userRole === Global_Admin_Role && (<div className="input settings-select">
              <div className="select-company-d">
                <Select  options={companies.map((c) => ({ value: c.id, label: c.name}))} isSearchable={true} placeholder="Please select a company" styles={customStyles} 
                         value={companies.map((c) => ({ value: c.id, label: c.name })).find((opt) => opt.value === companyId) || null} onChange={handleChange}  isDisabled={deviceId !== "new"}/>
              </div>
            </div>)}
            <div style={{ height: "1px", backgroundColor: "#ccc", margin: "5px 0" }}></div>
            <div className="input">
              <span style={{padding:"30px", fontWeight: "800", color: "#797979"}}>
                CH1
              </span>
              <input placeholder="Channel name 1" onChange={(e) => setChannelName1(e.target.value)} value={channelName1}/>
            </div>
            <div className="input">
              <span style={{padding:"30px", fontWeight: "800", color: "#797979"}}>
                CH2
              </span>
              <input placeholder="Channel name 2" onChange={(e) => setChannelName2(e.target.value)} value={channelName2}/>
            </div>
            <div className="input">
              <span style={{padding:"30px", fontWeight: "800", color: "#797979"}}>
                CH3
              </span>
              <input placeholder="Channel name 3" onChange={(e) => setChannelName3(e.target.value)} value={channelName3}/>
            </div>
            <div className="input">
              <span style={{padding:"30px", fontWeight: "800", color: "#797979"}}>
                CH4
              </span>
              <input placeholder="Channel name 4" onChange={(e) => setChannelName4(e.target.value)} value={channelName4}/>
            </div>
           {errorMessage && (
            <div className="error input">
              <span style={{paddingLeft: "10px"}}>{errorMessage}</span>
            </div>
           )}
           <div className="submit-container">
            <div className="cancel" onClick={handleCancelClick}>Cancel</div>
            {!loadingSave && (<div className="submit" onClick={handleSaveClick}>Save</div>)}
            {loadingSave && (<div className="submit">Loading...</div>)}
           </div>
        </div>
      </div>
       </div> 
  );
}

export default Device;